import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RadioTower } from 'lucide-react';
import { matchingAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

// Prompt AI: read-only Stall Inspector for services/receipt_paid_
// reconciliation.py's Preview/Apply pass. Third prod stall (168/270,
// frozen; earlier 75/200, 25/271) left the operator with no way to name the
// stuck receipt -- this card is that observability. The operator read for a
// future stall is: open this card, hit Refresh, read seconds-since-
// heartbeat + current receipt id + stage directly off the job doc -- never
// a guess.

function secondsSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return ms >= 0 ? Math.round(ms / 1000) : null;
}

function JobSection({ label, job }) {
  if (!job) {
    return (
      <div className="text-xs text-muted-foreground">
        <span className="font-medium">{label}:</span> no run recorded yet
      </div>
    );
  }
  const progress = job.progress || {};
  const heartbeatAgeS = secondsSince(progress.heartbeat_at);
  const current = progress.current_receipt;
  const ring = progress.recent_receipts || [];

  return (
    <div className="space-y-1 border rounded p-2" data-testid={`receipt-paid-inspector-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-xs">{label}</span>
        <Badge variant={job.status === 'running' ? 'default' : 'outline'} className="text-[10px]">
          {job.status}
        </Badge>
        {job.status === 'running' && (
          <span className="text-[10px] text-muted-foreground">started {job.started_at}</span>
        )}
        {job.error && <span className="text-[10px] text-red-600 dark:text-red-400">{job.error}</span>}
      </div>
      {progress.scanned != null && (
        <p className="text-xs text-muted-foreground">
          Scanned {progress.scanned} of {progress.total ?? '?'} ({progress.seen ?? 0} in population)
        </p>
      )}
      {job.status === 'running' && (
        <p className="text-xs" data-testid={`receipt-paid-inspector-${label.toLowerCase()}-heartbeat`}>
          <strong>Heartbeat:</strong>{' '}
          {heartbeatAgeS != null ? `${heartbeatAgeS}s ago` : 'never'}
          {current ? (
            <>
              {' · '}
              <strong>Current receipt:</strong> {current.invoice_id} · stage {current.stage} · {Math.round(current.elapsed_ms)}ms
            </>
          ) : (
            ' · between receipts'
          )}
        </p>
      )}
      {ring.length > 0 && (
        <ul className="text-[10px] font-mono text-muted-foreground space-y-0.5">
          {ring.slice(-5).reverse().map((r, i) => (
            <li key={i}>
              {r.invoice_id} · {r.stage} · {Math.round(r.elapsed_ms)}ms · {r.outcome}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ReceiptPaidJobInspectorPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releasePreview, setReleasePreview] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await matchingAPI.receiptPaidJobInspector();
      setData(resp);
      setReleasePreview(null);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load job inspector.');
    } finally {
      setLoading(false);
    }
  };

  const previewRelease = async () => {
    if (!data?.running_operation) return;
    setReleasing(true);
    try {
      const { data: resp } = await matchingAPI.receiptPaidJobInspectorRelease(data.running_operation, true);
      setReleasePreview(resp);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to preview release.');
    } finally {
      setReleasing(false);
    }
  };

  const applyRelease = async () => {
    if (!data?.running_operation) return;
    if (!window.confirm(
      `Mark the stuck "${data.running_operation}" job stale and release it? It will be visible ` +
      'as failed (released_by_operator), never deleted, and a new Preview/Apply can start immediately.'
    )) return;
    setReleasing(true);
    try {
      await matchingAPI.receiptPaidJobInspectorRelease(data.running_operation, false);
      await refresh();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to release job.');
    } finally {
      setReleasing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RadioTower className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Receipt-Paid Job Inspector</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only mid-pass stall observability for the Receipt-Paid Reconciliation Preview/Apply
          pass -- names the current receipt id and stage from the job doc's own heartbeat, so a
          future stall never again requires guessing which receipt froze it. A parked-await stall
          produces no loop-lag watchdog entry (that watchdog only ever fires on a synchronous CPU
          hold) -- an empty watchdog section below is expected, not exonerating; read the
          heartbeat above it first.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="receipt-paid-inspector-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Refresh
        </Button>

        {error && (
          <div className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-2" data-testid="receipt-paid-inspector-result">
            <JobSection label="Apply" job={data.apply_job} />
            <JobSection label="Preview" job={data.preview_job} />

            <div className="text-xs text-muted-foreground">
              <strong>Loop-lag watchdog:</strong>{' '}
              {data.watchdog?.is_running ? 'running' : 'not running'} ·{' '}
              {(data.watchdog?.recent_holds || []).length} recent hold(s)
              {(data.watchdog?.recent_holds || []).length > 0 && (
                <ul className="text-[10px] font-mono mt-1 space-y-0.5">
                  {data.watchdog.recent_holds.slice(-5).map((h, i) => (
                    <li key={i}>{h.at} · {h.duration_s}s · {h.culprit || 'unnamed frame'}</li>
                  ))}
                </ul>
              )}
            </div>

            {data.running_operation && (
              <div className="space-y-2 border-t pt-2">
                <p className="text-xs text-muted-foreground">
                  A "{data.running_operation}" job is currently running. If it is genuinely stuck
                  (heartbeat not advancing), mark it stale to release it early instead of waiting
                  out the 15-minute guard.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm" onClick={previewRelease} disabled={releasing}
                    data-testid="receipt-paid-inspector-release-preview"
                  >
                    Preview release
                  </Button>
                  {releasePreview && !releasePreview.released && releasePreview.would_release && (
                    <Button
                      variant="destructive" size="sm" onClick={applyRelease} disabled={releasing}
                      data-testid="receipt-paid-inspector-release-apply"
                    >
                      {releasing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Mark stale / release
                    </Button>
                  )}
                </div>
                {releasePreview?.would_release && (
                  <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="receipt-paid-inspector-release-preview-result">
                    Would release job {releasePreview.would_release.job_id} (started {releasePreview.would_release.started_at})
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
